/**
 * Fleet dashboard (TMMPDM-0006 / 0020): GET /v1/admin/dashboard for the selected period + reseller.
 * TMMPDM-0038: the unpaid reseller charges strip, per currency, when there are any.
 * TMMPDM-0035: `period` and `reseller` live in the URL; the scope and "Loaded at" lines sit under
 * the heading.
 */
import { useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { ErrorState, PageHeader } from '@/components/page';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { periodFor, type PeriodKey } from './dashboard/period';
import {
  PERIOD_KEYS,
  PERIOD_VIEW,
  loadedAtLabel,
  periodScope,
  type PeriodView,
} from './dashboard/periodView';
import { useFleetDashboard } from './dashboard/useFleetDashboard';
import { FleetKpis } from './dashboard/FleetKpis';
import { RetailByTenantChart } from './dashboard/RetailByTenantChart';
import { ActiveSubscribersChart } from './dashboard/ActiveSubscribersChart';
import { FleetTable } from './dashboard/FleetTable';
import { AffiliateBonusesTable } from './dashboard/AffiliateBonusesTable';
import { ByResellerTable } from './dashboard/ByResellerTable';
import { ResellersByStandingStrip } from './dashboard/ResellersByStandingStrip';
import { RetainedMarginStrip } from './dashboard/RetainedMarginStrip';
import { UnpaidResellerChargesStrip } from './dashboard/UnpaidResellerChargesStrip';
import {
  resellerFilterOptions,
  resellerScope,
  useResellerNames,
} from './resellers/useResellerNames';

const DASH_TABS = ['trends', 'tenants', 'resellers', 'affiliates'] as const;
type DashTab = (typeof DASH_TABS)[number];
type DashboardView = PeriodView & { tab: DashTab };

/** Period and reseller stay shared with Revenue. `tab` is the dashboard's own. */
const DASHBOARD_VIEW: UrlSchema<DashboardView> = {
  ...PERIOD_VIEW,
  tab: { default: 'trends', parse: oneOf(DASH_TABS) },
};

export function Dashboard() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [view, setView] = useUrlState(DASHBOARD_VIEW);
  const { period: periodKey, reseller: resellerId } = view;
  const period = useMemo(() => periodFor(periodKey), [periodKey]);
  const { resellers, names: resellerNames } = useResellerNames();
  const { data, loading, error, failure, refetch } = useFleetDashboard(period, resellerId);

  return (
    <Box>
      <PageHeader
        title={t('page.dashboard.title', 'Dashboard')}
        subtitle={t(
          'page.dashboard.subtitle',
          'Every tenant on the platform, what they collected and what we keep.',
        )}
        scope={periodScope(
          t,
          i18n.language,
          periodKey,
          period,
          resellerScope(t, resellerNames, resellerId),
        )}
        meta={
          data ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {loadedAtLabel(t, i18n.language, data.loadedAt)}
            </Typography>
          ) : null
        }
        controls={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="dashboard-reseller-label">
                {t('partners.singular', 'Partner')}
              </InputLabel>
              <Select
                labelId="dashboard-reseller-label"
                value={resellerId === undefined ? 'ALL' : String(resellerId)}
                label={t('partners.singular', 'Partner')}
                onChange={(event) =>
                  setView({
                    reseller: event.target.value === 'ALL' ? undefined : Number(event.target.value),
                  })
                }
              >
                <MenuItem value="ALL">{t('dashboard.resellerFilter.all', 'All')}</MenuItem>
                {resellerFilterOptions(t, resellers, resellerId).map((reseller) => (
                  <MenuItem key={reseller.id} value={String(reseller.id)}>
                    {reseller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="dashboard-period-label">
                {t('dashboard.period.label', 'Period')}
              </InputLabel>
              <Select
                labelId="dashboard-period-label"
                value={periodKey}
                label={t('dashboard.period.label', 'Period')}
                onChange={(event) => setView({ period: event.target.value as PeriodKey })}
              >
                {PERIOD_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`dashboard.period.${key}`, key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      />

      {error ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={failure}
            message={t('dashboard.loadError', 'Could not load the fleet dashboard.')}
            onRetry={refetch}
          />
        </Box>
      ) : null}

      <FleetKpis data={data} loading={loading} locale={i18n.language} />

      {data ? (
        <>
          <RetainedMarginStrip rows={data.dashboard.retainedMargin ?? []} locale={i18n.language} />
          <ResellersByStandingStrip counts={data.dashboard.resellersByState ?? []} />
          <UnpaidResellerChargesStrip
            rows={data.dashboard.resellerChargesUnpaid ?? []}
            locale={i18n.language}
          />
        </>
      ) : null}

      <Box sx={{ mt: 2 }}>
        <FilterTabs
          ariaLabel={t('dashboard.tabs.aria', 'Dashboard sections')}
          active={view.tab}
          onChange={(next) => {
            const tab = oneOf(DASH_TABS)(next);
            if (tab) setView({ tab });
          }}
          tabs={[
            { key: 'trends', label: t('dashboard.tabs.trends', 'Trends') },
            { key: 'tenants', label: t('dashboard.tabs.tenants', 'Tenants') },
            { key: 'resellers', label: t('dashboard.tabs.resellers', 'Resellers') },
            {
              key: 'affiliates',
              label: t('dashboard.affiliateBonuses.title', 'Affiliate bonuses'),
            },
          ]}
        />
      </Box>

      {view.tab === 'trends' ? (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ my: 3 }}>
          <RetailByTenantChart
            data={data}
            loading={loading}
            period={period}
            locale={i18n.language}
          />
          <ActiveSubscribersChart
            data={data}
            loading={loading}
            period={period}
            locale={i18n.language}
          />
        </Stack>
      ) : null}

      {view.tab === 'tenants' ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'dashboard.fleet.subtitle',
              "Retail is what subscribers paid. Platform is what we keep after the tenant's share.",
            )}
          </Typography>
          <FleetTable
            data={data}
            loading={loading}
            error={error}
            failure={failure}
            onRetry={refetch}
            locale={i18n.language}
          />
        </Box>
      ) : null}

      {view.tab === 'resellers' ? (
        <Box sx={{ mt: 2 }}>
          <ByResellerTable data={data} locale={i18n.language} />
        </Box>
      ) : null}

      {view.tab === 'affiliates' ? (
        <Box sx={{ mt: 2 }}>
          <AffiliateBonusesTable data={data} locale={i18n.language} />
        </Box>
      ) : null}
    </Box>
  );
}
